import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useQueryClient, type QueryKey } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import type { AssistantAttachmentView } from '@repo/types'
import { api } from '@/lib/api'
import { getMe } from '@/lib/auth'
import { useAuth } from '@/contexts/AuthContext'
import { streamAssistantChat, type SseEvent } from '@/lib/assistant-stream'
import { dealKeys } from '@/hooks/api/deal-keys'
import { eventKeys } from '@/hooks/api/events'
import { taskKeys } from '@/hooks/api/tasks'
import { contactKeys } from '@/hooks/api/contacts'
import { propertyKeys } from '@/hooks/api/properties'
import { emailKeys } from '@/hooks/api/emails'
import { callLogKeys } from '@/hooks/api/call-logs'
import { dashboardKeys } from '@/hooks/api/dashboard'
import { reportKeys } from '@/hooks/api/reports'
import { ASSISTANT_CHAT_ID_KEY } from './assistant-roles'

export interface ChatBubble {
  id: string
  role: 'user' | 'assistant'
  text: string
  isError?: boolean
  attachments?: AssistantAttachmentView[]
}

export interface EmailDraft {
  toolUseId: string
  draft: { accountId: string; to: string[]; cc?: string[]; subject: string; bodyHtml: string }
}

/**
 * Zápisové nástroje → prefixy query kľúčov, ktoré treba po odpovedi obnoviť
 * (otvorená stránka ukáže zmenu). Obchody: zoznam ['my-deals'], pipeline ['pipeline'],
 * detail ['deal', id], dashboard ['dashboard-stats'], reporty ['deal-stats'],
 * changelog štádií (Môj tím) ['deal-stage-changelog'], rozdelenie provízie
 * ['commission-splits'] (uzavretie ho zakladá). Nehnuteľnosti: koncové štádium ju označí SOLD,
 * strata/obnova vráti ACTIVE → zoznam ['properties'], detail ['property'], história
 * ['property-history']. E-mail: odoslaná správa → zoznamy ['email-messages'] a komunikácia
 * pri kontakte/obchode ['comm-emails']. Úlohy: ['tasks'] kryje aj
 * kalendárové úlohy (['tasks', 'calendar', …]). Udalosti: ['events'] kryje aj históriu
 * udalostí pri entitách. Úlohy aj udalosti sú navyše v detaile obchodu a na dashboarde
 * (rovnako ako v hookoch tasks.ts / events.ts). Kontakty = jedna osoba CRM pod ['contacts'].
 */
const DEAL_KEYS: QueryKey[] = [
  dealKeys.lists, dealKeys.pipeline, dealKeys.details, dashboardKeys.stats, ['deal-stats'], ['deal-stage-changelog'],
  ['commission-splits'], propertyKeys.lists, ['property'], ['property-history'],
]
const WRITE_TOOL_KEYS: Record<string, QueryKey[]> = {
  move_deal_stage: DEAL_KEYS,
  mark_deal_lost: DEAL_KEYS,
  restore_deal_lost: DEAL_KEYS,
  create_event: [eventKeys.all, dealKeys.details, dashboardKeys.stats],
  update_event: [eventKeys.all, dealKeys.details, dashboardKeys.stats],
  update_event_note: [eventKeys.all, dealKeys.details],
  create_task: [taskKeys.all, dealKeys.details, dashboardKeys.stats],
  update_task: [taskKeys.all, dealKeys.details, dashboardKeys.stats],
  // Nová osoba mení aj KPI Reportov a počty dashboardu (rovnako ako invalidateContactViews).
  create_contact: [contactKeys.all, reportKeys.all, dashboardKeys.stats],
  // Poznámka linkovaného hovoru je poznámka kontaktu — obnoviť aj jeho detail.
  update_call_note: [callLogKeys.all, contactKeys.all],
  send_email: [emailKeys.messages, ['comm-emails']],
  // Dokument nehnuteľnosti je médium typu DOCUMENT, dokument obchodu je v jeho detaile.
  save_attachment: [dealKeys.details, ['property'], ['property-media'], ['property-history']],
}

const readStoredChatId = (): string | undefined => {
  try { return localStorage.getItem(ASSISTANT_CHAT_ID_KEY) ?? undefined } catch { return undefined }
}
const storeChatId = (id: string | undefined) => {
  try {
    if (id) localStorage.setItem(ASSISTANT_CHAT_ID_KEY, id)
    else localStorage.removeItem(ASSISTANT_CHAT_ID_KEY)
  } catch { /* private mode / zablokované úložisko */ }
}

const FALLBACK_ERROR = 'Neočakávaná chyba asistenta.'
const LOAD_ERROR = 'Chat sa nepodarilo načítať.'

function isEmailDraft(draft: unknown): draft is EmailDraft['draft'] {
  if (!draft || typeof draft !== 'object') return false
  const d = draft as Record<string, unknown>
  // EmailConfirmCard renderuje priamo to/cc (join), subject a bodyHtml.
  return (
    Array.isArray(d.to) &&
    (d.cc === undefined || Array.isArray(d.cc)) &&
    typeof d.subject === 'string' &&
    typeof d.bodyHtml === 'string' &&
    (d.accountId === undefined || typeof d.accountId === 'string')
  )
}

let seq = 0
const nextId = () => `a${++seq}`

/**
 * Uložená história (GET /assistant/chats/:id → `toDisplayMessages` na serveri) → bubliny.
 * Tvar: `{ role: 'user', content: string, attachments? }` | `{ role: 'assistant', content: [{ type: 'text', text }] }`.
 */
function toBubbles(messages: unknown[]): ChatBubble[] {
  const out: ChatBubble[] = []
  for (const m of messages as Array<{ role?: string; content?: unknown; attachments?: unknown }>) {
    if (m?.role === 'user' && typeof m.content === 'string') {
      const attachments = Array.isArray(m.attachments) && m.attachments.length > 0 ? (m.attachments as AssistantAttachmentView[]) : undefined
      out.push({ id: nextId(), role: 'user', text: m.content, ...(attachments && { attachments }) })
    } else if (m?.role === 'assistant' && Array.isArray(m.content)) {
      const text = (m.content as Array<{ type?: string; text?: string }>)
        .filter((b) => b?.type === 'text' && typeof b.text === 'string')
        .map((b) => b.text)
        .join('')
      if (text) out.push({ id: nextId(), role: 'assistant', text })
    }
  }
  return out
}

export interface AssistantContextValue {
  open: boolean
  setOpen: (open: boolean) => void
  bubbles: ChatBubble[]
  /** Popisok práve bežiaceho nástroja (SSE `tool` → `label`), inak null. */
  activity: string | null
  pendingEmail: EmailDraft | null
  isStreaming: boolean
  /** Načítava sa uložený chat (loadChat / obnova po reloade) — send/respondToEmail sa vtedy ignorujú. */
  isLoadingChat: boolean
  chatId: string | undefined
  /** Text aj prílohy sú nepovinné — prázdna správa (bez oboch) sa ignoruje. */
  send: (text: string, attachments?: AssistantAttachmentView[]) => void
  respondToEmail: (toolUseId: string, approved: boolean) => void
  /** Nový chat — zruší bežiaci stream a zabudne chatId. */
  reset: () => void
  /**
   * Otvorí uložený chat (z histórie). Neexistujúci/cudzí chat (404) → prázdny nový chat;
   * iná chyba → chybová bublina, uložené chatId ostáva.
   */
  loadChat: (id: string) => Promise<void>
}

const AssistantContext = createContext<AssistantContextValue | null>(null)

export function AssistantProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const { setUser } = useAuth()
  const [open, setOpen] = useState(false)
  const [bubbles, setBubbles] = useState<ChatBubble[]>([])
  const [activity, setActivity] = useState<string | null>(null)
  const [pendingEmail, setPendingEmail] = useState<EmailDraft | null>(null)
  const [isStreaming, setIsStreaming] = useState(false)
  const [isLoadingChat, setIsLoadingChat] = useState(false)
  const [chatId, setChatIdState] = useState<string | undefined>(readStoredChatId)
  const chatIdRef = useRef(chatId)
  const abortRef = useRef<AbortController | null>(null)
  const streamingRef = useRef(false)
  const loadingRef = useRef(false)
  const restoredRef = useRef(false)
  const restoreFailedRef = useRef(false)
  const bubblesRef = useRef(bubbles)
  bubblesRef.current = bubbles
  // Každá user akcia (run/reset/loadChat) zvýši generáciu — oneskorené výsledky starších akcií sa zahodia.
  const generationRef = useRef(0)

  const setChatId = useCallback((id: string | undefined) => {
    chatIdRef.current = id
    setChatIdState(id)
    storeChatId(id)
  }, [])

  const setStreaming = useCallback((value: boolean) => {
    streamingRef.current = value
    setIsStreaming(value)
  }, [])

  const setLoading = useCallback((value: boolean) => {
    loadingRef.current = value
    setIsLoadingChat(value)
  }, [])

  /** Zruší bežiaci stream/načítanie a zneplatní čakajúce akcie; stav si nastaví volajúci. */
  const cancelCurrent = useCallback(() => {
    generationRef.current++
    abortRef.current?.abort()
    abortRef.current = null
    setLoading(false)
  }, [setLoading])

  /** `newBubble` = delta začína novú správu asistenta (server každú iteráciu po nástroji ukladá zvlášť). */
  const appendDelta = useCallback((delta: string, newBubble: boolean) => {
    setBubbles((prev) => {
      const last = prev[prev.length - 1]
      if (!newBubble && last?.role === 'assistant' && !last.isError) {
        return [...prev.slice(0, -1), { ...last, text: last.text + delta }]
      }
      return [...prev, { id: nextId(), role: 'assistant', text: delta }]
    })
  }, [])

  const run = useCallback(async (payload: object) => {
    cancelCurrent()
    const controller = new AbortController()
    abortRef.current = controller
    setStreaming(true)
    setActivity(null)
    setPendingEmail(null)
    const touched = new Set<string>()
    // Po nástroji ide text do novej bubliny — rovnako ako po reloade (každá iterácia = samostatná správa).
    // Štart na true: nový ťah (aj pokračovanie po zamietnutí e-mailu, ktoré `tool` event nepošle)
    // je vždy nová správa asistenta.
    let breakNext = true

    // streamAssistantChat nikdy nezlyhá — každá chyba príde ako jeden `error` event,
    // zrušenie (abort) je tiché.
    const onEvent = ({ event, data }: SseEvent) => {
      if (controller.signal.aborted) return
      if (typeof data?.chatId === 'string') setChatId(data.chatId)
      switch (event) {
        case 'text':
          setActivity(null)
          if (typeof data?.delta === 'string' && data.delta) {
            appendDelta(data.delta, breakNext)
            breakNext = false
          }
          break
        case 'tool':
          breakNext = true
          setActivity(typeof data?.label === 'string' ? data.label : null)
          if (typeof data?.name === 'string') touched.add(data.name)
          break
        case 'confirmation_required':
          breakNext = true
          setActivity(null)
          if (typeof data?.toolUseId === 'string' && isEmailDraft(data?.draft)) {
            setPendingEmail({ toolUseId: data.toolUseId, draft: data.draft })
          } else {
            setBubbles((prev) => [...prev, { id: nextId(), role: 'assistant', text: 'Návrh e-mailu sa nepodarilo zobraziť.', isError: true }])
          }
          break
        case 'error':
          setBubbles((prev) => [
            ...prev,
            { id: nextId(), role: 'assistant', text: typeof data?.message === 'string' ? data.message : FALLBACK_ERROR, isError: true },
          ])
          // Súhlas bol odvolaný / asistent vypnutý medzičasom (iná karta, Môj tím) — obnov usera,
          // aby sa zobrazila súhlasová brána, resp. panel zmizol (AssistantPanel číta user).
          if (data?.code === 'AI_CONSENT_REQUIRED' || data?.code === 'AI_ASSISTANT_DISABLED') {
            void getMe().then(setUser).catch(() => {})
          }
          break
      }
    }

    try {
      await streamAssistantChat(payload, onEvent, controller.signal)
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null
        setStreaming(false)
        setActivity(null)
      }
      // Aj pri zrušení — zápis na serveri už mohol prebehnúť. Po abort-e server nástroj
      // ešte môže dokončovať, takže invalidácia ho môže predbehnúť (akceptované — ďalší
      // refetch stránky zmenu dobehne).
      for (const name of touched) {
        for (const queryKey of WRITE_TOOL_KEYS[name] ?? []) void queryClient.invalidateQueries({ queryKey })
      }
      // Ťah mohol založiť chat alebo zmeniť jeho titulok/poradie v histórii.
      void queryClient.invalidateQueries({ queryKey: ['assistant-chats'] })
    }
  }, [appendDelta, cancelCurrent, queryClient, setChatId, setStreaming])

  const send = useCallback((text: string, attachments: AssistantAttachmentView[] = []) => {
    const trimmed = text.trim()
    if ((!trimmed && attachments.length === 0) || streamingRef.current || loadingRef.current) return
    setBubbles((prev) => [...prev, { id: nextId(), role: 'user', text: trimmed, ...(attachments.length > 0 && { attachments }) }])
    void run({
      ...(trimmed && { message: trimmed }),
      ...(attachments.length > 0 && { attachmentIds: attachments.map((a) => a.id) }),
      chatId: chatIdRef.current,
    })
  }, [run])

  const respondToEmail = useCallback((toolUseId: string, approved: boolean) => {
    if (streamingRef.current || loadingRef.current) return
    setPendingEmail(null)
    void run({ approval: { toolUseId, approved }, chatId: chatIdRef.current })
  }, [run])

  const reset = useCallback(() => {
    cancelCurrent()
    setChatId(undefined)
    setBubbles([])
    setPendingEmail(null)
    setActivity(null)
    setStreaming(false)
  }, [cancelCurrent, setChatId, setStreaming])

  /** Vráti false pri inej chybe než 404 (chat ostáva uložený, dá sa skúsiť znova). */
  const loadChatInner = useCallback(async (id: string): Promise<boolean> => {
    cancelCurrent()
    const generation = generationRef.current
    setLoading(true)
    setStreaming(false)
    setActivity(null)
    setPendingEmail(null)
    try {
      // 404 (zmazaný chat) ticho, inú chybu ukáže bublina LOAD_ERROR.
      const { data } = await api.get(`/assistant/chats/${id}`, { skipErrorToast: true })
      if (generation !== generationRef.current) return true
      setChatId(data?.id ?? id)
      setBubbles(toBubbles(Array.isArray(data?.messages) ? data.messages : []))
      return true
    } catch (e) {
      if (generation !== generationRef.current) return true
      if (isAxiosError(e) && e.response?.status === 404) {
        // zmazaný/cudzí chat — začni nanovo
        setChatId(undefined)
        setBubbles([])
        return true
      }
      setChatId(id)
      setBubbles([{ id: nextId(), role: 'assistant', text: LOAD_ERROR, isError: true }])
      return false
    } finally {
      if (generation === generationRef.current) setLoading(false)
    }
  }, [cancelCurrent, setChatId, setLoading, setStreaming])

  const loadChat = useCallback(async (id: string) => {
    await loadChatInner(id)
  }, [loadChatInner])

  // Po reloade stránky obnov rozpracovaný chat pri prvom otvorení panelu. Pri zlyhaní
  // (nie 404) sa obnova skúsi znova až pri ďalšom otvorení — nie pri každom renderi.
  useEffect(() => {
    if (!open || restoredRef.current) return
    restoredRef.current = true
    const id = chatIdRef.current
    if (id && bubblesRef.current.every((b) => b.isError)) {
      void loadChatInner(id).then((ok) => {
        if (!ok) restoreFailedRef.current = true
      })
    }
  }, [open, loadChatInner])

  useEffect(() => {
    if (!open && restoreFailedRef.current) {
      restoreFailedRef.current = false
      restoredRef.current = false
    }
  }, [open])

  useEffect(() => () => abortRef.current?.abort(), [])

  const value = useMemo<AssistantContextValue>(() => ({
    open, setOpen, bubbles, activity, pendingEmail, isStreaming, isLoadingChat, chatId, send, respondToEmail, reset, loadChat,
  }), [open, bubbles, activity, pendingEmail, isStreaming, isLoadingChat, chatId, send, respondToEmail, reset, loadChat])

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>
}

export function useAssistant(): AssistantContextValue {
  const ctx = useContext(AssistantContext)
  if (!ctx) throw new Error('useAssistant must be used within AssistantProvider')
  return ctx
}
